/**
 * Public-side Content API client.
 *
 * Consumed by `apps/public/src/App.tsx` (Phase K+2 refactor will wire it up
 * surface by surface). Phase 4 stages this client without altering App.tsx
 * so the visual diff stays empty.
 *
 * Properties:
 *   - Zod-validates every response — refuse malformed data.
 *   - Uses `fetch` with `cache: 'default'` so the browser honours the
 *     source's `Cache-Control: public, max-age=300,
 *     stale-while-revalidate=600` and REVALIDATES once a cached copy goes
 *     stale. The original `cache: 'force-cache'` was wrong: per the fetch
 *     spec it returns a matching cached response whether fresh or STALE,
 *     so a session that had ever cached `flows/index.json` (or any
 *     flow/landing doc) never revalidated — published snapshot edits never
 *     surfaced in that session, contradicting the "≤5 min via max-age=300"
 *     publish contract (`apps/admin/lib/publish/publishSnapshot.ts`).
 *   - Distinguishes 404 (returns null) from other errors (throws).
 *   - Never logs PII in error messages.
 *
 * Env var: `VITE_CONTENT_API_BASE_URL` (e.g. `http://localhost:3001/api`
 * for dev; `/api` for same-origin prod).
 *
 * # Two read modes (N1b part 2b)
 *
 *   - **Live admin API** — base ends in `/api` (e.g. `https://admin…/api`).
 *     Logical paths map 1:1 (`/flows`, `/flows/spring`, `/landing`,
 *     `/landing/hero`). Used now only as the offline / cold fallback; it was
 *     the live source before the read-layer decouple.
 *   - **Static snapshot CDN** — base is a Blob `content/` prefix (e.g.
 *     `https://…blob.core.windows.net/media/content`, no `/api` suffix). The
 *     client maps each logical path to a static file: `/flows` →
 *     `flows/index.json`, `/flows/spring` → `flows/spring.json`, `/landing`
 *     → `landing/index.json`, `/landing/hero` → `landing/hero.json`. This is
 *     the LIVE source — the public bundle reads it directly off the CDN and
 *     never waits on the admin Container App cold start.
 */

import {
  // N4a Fix #8 (GDPR): the public CDN snapshot strips `updatedBy` (editor
  // email), so the public bundle validates against the PUBLIC projections
  // (updatedBy optional). The admin-API fallback (which still carries
  // updatedBy) also validates against these.
  PublicFlowDocSchema,
  PublicLandingDocSchema,
  AboutContentSchema,
  CategorySchema,
  ContactContentSchema,
  DefaultsContentSchema,
  EmailTemplateContentSchema,
  HeroContentSchema,
  ModalsContentSchema,
  type FlowDoc,
  type FlowSummary,
  type LandingDoc,
  type LandingSurface,
  type SiteContent,
} from '@brandbox/content-schema';
import { z } from 'zod';

const FlowSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(['draft', 'published', 'archived']),
  // Slice 2 pivot — public landing partitions flows by visibility. The
  // server materialises the field via `?? 'coming-soon'` so it's
  // always present in the response.
  visibility: z.enum(['hidden', 'coming-soon', 'live']),
  // Slice 2 pivot — surfaced in the summary so we can render the
  // appended tile's picture without round-tripping the full flow doc.
  heroImage: z.string().optional(),
  version: z.number().int().nonnegative(),
  optionCount: z.number().int().nonnegative(),
  stepCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // N4a Fix #8 (GDPR): `updatedBy` (an editor email) is STRIPPED from the
  // public CDN snapshot, so it is OPTIONAL here — the public bundle never
  // renders it. Kept in the schema (optional) so the admin-API fallback
  // response, which still carries it, also validates.
  updatedBy: z.string().email().optional(),
});

const FlowsResponseSchema = z.object({
  flows: z.array(FlowSummarySchema),
});

const SiteContentSchema = z.object({
  hero: HeroContentSchema.optional(),
  about: AboutContentSchema.optional(),
  categories: z.array(CategorySchema).optional(),
  contact: ContactContentSchema.optional(),
  modals: ModalsContentSchema.optional(),
  emailTemplate: EmailTemplateContentSchema.optional(),
  defaults: DefaultsContentSchema.optional(),
});

export class ContentApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'ContentApiError';
  }
}

export interface ContentApiClient {
  getFlows(): Promise<FlowSummary[]>;
  getFlow(id: string): Promise<FlowDoc | null>;
  /**
   * Without `surface`: fetch the joined `SiteContent` aggregate.
   * With `surface`: fetch a single `LandingDoc`.
   */
  getLanding(surface?: LandingSurface): Promise<SiteContent | LandingDoc | null>;
}

export interface ContentApiClientOptions {
  /** Base URL, e.g. "http://localhost:3001/api". */
  readonly baseUrl?: string;
  /** Override the default fetch (testing). */
  readonly fetch?: typeof fetch;
}

const DEFAULT_BASE_URL = readEnvBaseUrl() ?? 'http://localhost:3001/api';

function readEnvBaseUrl(): string | undefined {
  // Vite exposes env via import.meta.env at build time; guard for non-browser.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (import.meta as any).env as Record<string, string> | undefined;
    return meta?.['VITE_CONTENT_API_BASE_URL'];
  } catch {
    return undefined;
  }
}

/**
 * Is this base URL the live admin API (ends in `/api`) vs the static snapshot
 * CDN (a `content/` prefix)? Determines how logical paths are resolved.
 */
function isApiMode(baseUrl: string): boolean {
  return /\/api$/.test(baseUrl);
}

/**
 * Map a logical resource path (`/flows`, `/flows/spring`, `/landing`,
 * `/landing/hero`) onto the request path for the active mode.
 *
 *   API mode (live admin):  unchanged (`/flows`, `/flows/spring`, …).
 *   Static mode (CDN):      `/flows` → `/flows/index.json`,
 *                           `/flows/<id>` → `/flows/<id>.json`,
 *                           `/landing` → `/landing/index.json`,
 *                           `/landing/<surface>` → `/landing/<surface>.json`.
 */
export function resolveResourcePath(baseUrl: string, logicalPath: string): string {
  if (isApiMode(baseUrl)) return logicalPath;
  // Static snapshot mode. A collection root (`/flows`, `/landing`) becomes
  // `<root>/index.json`; a single resource becomes `<path>.json`.
  if (logicalPath === '/flows' || logicalPath === '/landing') {
    return `${logicalPath}/index.json`;
  }
  return `${logicalPath}.json`;
}

export function createContentApiClient(
  opts: ContentApiClientOptions = {},
): ContentApiClient {
  const baseUrl = (opts.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
  const doFetch = opts.fetch ?? globalThis.fetch;

  async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T | null> {
    const resourcePath = resolveResourcePath(baseUrl, path);
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${resourcePath}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        // `default` = standard HTTP cache semantics: serve a FRESH cached
        // copy, revalidate a STALE one. The blobs carry `max-age=300,
        // stale-while-revalidate=600`, so a published edit reaches the
        // browser within the documented ≤5-min window. NEVER `force-cache`
        // here — that mode also serves STALE entries without revalidating,
        // which pinned a session to its first-ever snapshot forever.
        cache: 'default',
      });
    } catch (err) {
      throw new ContentApiError(
        `network error fetching ${path}`,
        'NETWORK_ERROR',
        undefined,
      );
    }

    if (res.status === 404) return null;

    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('application/json')) {
      throw new ContentApiError(
        `${path} returned non-JSON (${res.status})`,
        'BAD_RESPONSE',
        res.status,
      );
    }

    const body: unknown = await res.json();
    if (!res.ok) {
      const code =
        typeof body === 'object' &&
        body !== null &&
        'error' in body &&
        typeof (body as { error: unknown }).error === 'object' &&
        (body as { error: { code?: string } }).error?.code
          ? (body as { error: { code: string } }).error.code
          : 'HTTP_ERROR';
      throw new ContentApiError(
        `${path} returned ${res.status}`,
        code,
        res.status,
      );
    }

    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      throw new ContentApiError(
        `${path} response failed schema validation`,
        'INVALID_RESPONSE',
        res.status,
      );
    }
    return parsed.data;
  }

  return {
    async getFlows(): Promise<FlowSummary[]> {
      const wrapped = await getJson('/flows', FlowsResponseSchema);
      return wrapped?.flows ?? [];
    },
    async getFlow(id: string): Promise<FlowDoc | null> {
      // PublicFlowDocSchema: updatedBy optional (stripped from the snapshot).
      return getJson(
        `/flows/${encodeURIComponent(id)}`,
        PublicFlowDocSchema as unknown as z.ZodType<FlowDoc>,
      );
    },
    async getLanding(
      surface?: LandingSurface,
    ): Promise<SiteContent | LandingDoc | null> {
      if (surface) {
        return getJson(
          `/landing/${encodeURIComponent(surface)}`,
          PublicLandingDocSchema as unknown as z.ZodType<LandingDoc>,
        );
      }
      const aggregate = await getJson('/landing', SiteContentSchema);
      // `SiteContent` declares hero/about/categories as required, but the
      // API legitimately omits surfaces that haven't been authored yet.
      // The schema marks them optional; we cast at the boundary so callers
      // can null-check the surfaces they consume.
      return aggregate as unknown as SiteContent | null;
    },
  };
}

/**
 * Shared singleton for callers that want module-level convenience. Tests
 * can swap with `createContentApiClient({ fetch })`.
 */
export const contentApi = createContentApiClient();
